import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { AuditPolicyData, AuditOwnershipChange, ComplianceStatus } from '@/hooks/useAuditData';
import { getComplianceStatus, getComplianceStatusLabel } from '@/hooks/useAuditData';
import { format } from 'date-fns';

// Veracord Brand Colors
const COLORS = {
  navy: [2, 7, 93] as [number, number, number],           // #02075d - Primary brand
  teal: [51, 153, 153] as [number, number, number],       // #339999 - Clinical Teal
  green: [51, 153, 51] as [number, number, number],       // #339933 - Approved/Current
  red: [220, 38, 38] as [number, number, number],         // #dc2626 - Overdue
  amber: [245, 158, 11] as [number, number, number],      // #f59e0b - Due Soon/Pending
  darkGray: [31, 41, 55] as [number, number, number],     // #1f2937 - Body text
  mediumGray: [100, 116, 139] as [number, number, number], // #64748b - Secondary text
  lightGray: [226, 232, 240] as [number, number, number], // #e2e8f0 - Borders
};

function getStatusColor(status: ComplianceStatus): [number, number, number] {
  switch (status) {
    case 'overdue':
      return COLORS.red;
    case 'due_soon':
      return COLORS.amber;
    case 'needs_ack':
      return COLORS.teal;
    case 'current':
      return COLORS.green;
  }
}

function formatCadence(months: number): string {
  if (months === 12) return '12 months';
  if (months === 6) return '6 months';
  if (months === 18) return '18 months';
  if (months === 24) return '24 months';
  if (months === 36) return '36 months';
  if (months % 12 === 0) return `${months / 12} years`;
  return `${months} months`;
}

// Map database status to display status
function getDisplayStatus(status: string): string {
  if (status === 'Approved' || status === 'Locked') {
    return 'Approved';
  }
  if (status === 'draft') {
    return 'Draft';
  }
  if (status === 'pending_approval') {
    return 'Pending Approval';
  }
  if (status === 'expired') {
    return 'Archived';
  }
  return status;
}

function addBrandedHeader(doc: jsPDF, title: string, clinicName: string, generatedBy?: string) {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Report title (main heading)
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text(title, pageWidth / 2, 20, { align: 'center' });
  
  // Clinic name
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.text(clinicName, pageWidth / 2, 28, { align: 'center' });
  
  // Teal divider line
  doc.setDrawColor(...COLORS.teal);
  doc.setLineWidth(1.5);
  doc.line(14, 34, pageWidth - 14, 34);
  
  // Metadata line
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.mediumGray);
  const generatedText = `Generated: ${format(new Date(), 'PPpp')}`;
  doc.text(generatedText, 14, 42);
  
  if (generatedBy) {
    doc.text(`Generated By: ${generatedBy}`, 14, 47);
    doc.text('Report Period: All Time', 14, 52);
    return 60; // Return Y position after header with generatedBy
  }
  
  doc.text('Report Period: All Time', 14, 47);
  return 55; // Return Y position after header
}

function addBrandedFooter(doc: jsPDF, clinicName: string) {
  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Teal divider line
    doc.setDrawColor(...COLORS.teal);
    doc.setLineWidth(0.5);
    doc.line(14, pageHeight - 22, pageWidth - 14, pageHeight - 22);
    
    // Veracord wordmark
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.navy);
    doc.text('Veracord™ — The System of Record', 14, pageHeight - 15);
    
    // Read-only notice and page number
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.mediumGray);
    doc.text('Read-only audit record', 14, pageHeight - 10);
    
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    
    // Clinic name and date on right
    doc.text(
      `${clinicName} • ${format(new Date(), 'PPP')}`,
      pageWidth - 14,
      pageHeight - 10,
      { align: 'right' }
    );
  }
}

function addSectionTitle(doc: jsPDF, title: string, yPos: number): number {
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.navy);
  doc.text(title, 14, yPos);
  
  // Teal underline
  doc.setDrawColor(...COLORS.teal);
  doc.setLineWidth(0.75);
  doc.line(14, yPos + 2, 14 + doc.getTextWidth(title), yPos + 2);
  
  return yPos + 8;
}

export function exportAuditPdf(
  policies: AuditPolicyData[],
  ownershipChanges: AuditOwnershipChange[],
  clinicName: string,
  generatedBy: string,
  ackStats?: Map<string, { pendingCount: number }>
) {
  const doc = new jsPDF();
  
  // Filter out draft policies from audit export
  const auditPolicies = policies.filter(p => p.status !== 'draft');
  
  // Header (includes metadata)
  let yPos = addBrandedHeader(doc, 'Compliance Audit Report', clinicName, generatedBy);

  // Summary Statistics Section
  yPos = addSectionTitle(doc, 'Summary Statistics', yPos);
  
  // Calculate stats - merge Locked into Approved count
  const totalPolicies = auditPolicies.length;
  const statusCounts = {
    Approved: auditPolicies.filter(p => p.status === 'Approved').length,
    pending_approval: auditPolicies.filter(p => p.status === 'pending_approval').length,
    Archived: auditPolicies.filter(p => p.status === 'expired').length,
  };
  
  // Calculate compliance stats
  const complianceCounts = { overdue: 0, due_soon: 0, needs_ack: 0, current: 0 };
  auditPolicies.forEach(p => {
    const pendingAckCount = p.requires_acknowledgement
      ? (ackStats?.get(p.id)?.pendingCount ?? 1)
      : 0;
    const status = getComplianceStatus(p, pendingAckCount);
    complianceCounts[status]++;
  });

  const totalVersions = auditPolicies.reduce((sum, p) => sum + p.versions.length, 0);
  const totalAcknowledgements = auditPolicies.reduce(
    (sum, p) => sum + p.versions.reduce((vSum, v) => vSum + v.acknowledgements.length, 0),
    0
  );

  // Stats table
  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Count', 'Metric', 'Count']],
    body: [
      ['Total Policies', totalPolicies.toString(), 'Approved', statusCounts.Approved.toString()],
      ['Total Versions', totalVersions.toString(), 'Pending Approval', statusCounts.pending_approval.toString()],
      ['Total Acknowledgements', totalAcknowledgements.toString(), 'Archived', statusCounts.Archived.toString()],
      ['Over Due', complianceCounts.overdue.toString(), 'Due Soon', complianceCounts.due_soon.toString()],
      ['Current', complianceCounts.current.toString(), 'Needs Acknowledgment', complianceCounts.needs_ack.toString()],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: COLORS.darkGray },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { cellWidth: 30 },
      2: { fontStyle: 'bold', cellWidth: 45 },
      3: { cellWidth: 30 },
    },
    margin: { left: 14, right: 14, bottom: 30 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 12;

  // Policy Details Table
  yPos = addSectionTitle(doc, 'Policy Details', yPos);

  const policyTableData = auditPolicies.map(p => {
    const pendingAckCount = p.requires_acknowledgement
      ? (ackStats?.get(p.id)?.pendingCount ?? 1)
      : 0;
    const complianceStatus = getComplianceStatus(p, pendingAckCount);
    return [
      p.name,
      p.category,
      getDisplayStatus(p.status),
      getComplianceStatusLabel(complianceStatus),
      p.owner_name,
      formatCadence(p.review_cadence_months),
      p.effective_date ? format(new Date(p.effective_date), 'PP') : 'N/A',
      format(new Date(p.next_review_date), 'PP'),
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Policy Name', 'Category', 'Status', 'Review Status', 'Assigned To', 'Cadence', 'Effective', 'Next Review']],
    body: policyTableData,
    theme: 'striped',
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 7, textColor: COLORS.darkGray },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 20 },
      2: { cellWidth: 18 },
      3: { cellWidth: 20 },
      4: { cellWidth: 25 },
      5: { cellWidth: 18 },
      6: { cellWidth: 22 },
      7: { cellWidth: 22 },
    },
    margin: { left: 14, right: 14, bottom: 30 },
    didParseCell: (data) => {
      // Color-code Review Status column
      if (data.section === 'body' && data.column.index === 3) {
        const statusText = data.cell.raw as string;
        if (statusText === 'Over Due') {
          data.cell.styles.textColor = COLORS.red;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Due Soon') {
          data.cell.styles.textColor = COLORS.amber;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Needs Ack') {
          data.cell.styles.textColor = COLORS.teal;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Current') {
          data.cell.styles.textColor = COLORS.green;
        }
      }
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 12;

  // Version History Section
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  yPos = addSectionTitle(doc, 'Version History', yPos);

  const versionTableData: string[][] = [];
  auditPolicies.forEach(policy => {
    policy.versions.forEach(version => {
      versionTableData.push([
        policy.name,
        `v${version.version_number}`,
        version.version_status.charAt(0).toUpperCase() + version.version_status.slice(1).replace('_', ' '),
        version.is_substantive ? 'Substantive' : 'Minor',
        version.created_by_name,
        format(new Date(version.created_at), 'PP'),
        version.approved_by_name || (version.version_status === 'rejected' ? `Rejected: ${version.rejected_by_name || 'Unknown'}` : '—'),
        version.rejection_reason || '—',
      ]);
    });
  });

  if (versionTableData.length === 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.mediumGray);
    doc.text('No version history recorded.', 14, yPos + 5);
    yPos += 15;
  } else {
    autoTable(doc, {
      startY: yPos,
      head: [['Policy', 'Version', 'Status', 'Type', 'Uploaded By', 'Upload Date', 'Approved/Rejected By', 'Rejection Reason']],
      body: versionTableData,
      theme: 'striped',
      headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7, textColor: COLORS.darkGray },
      columnStyles: {
        0: { cellWidth: 30 },
        7: { cellWidth: 35 },
      },
      margin: { left: 14, right: 14, bottom: 30 },
      didParseCell: (data) => {
        // Color-code version status
        if (data.section === 'body' && data.column.index === 2) {
          const statusText = (data.cell.raw as string).toLowerCase();
          if (statusText === 'approved') {
            data.cell.styles.textColor = COLORS.green;
          } else if (statusText === 'pending approval') {
            data.cell.styles.textColor = COLORS.amber;
          } else if (statusText === 'rejected') {
            data.cell.styles.textColor = COLORS.red;
          }
        }
      },
    });
    yPos = (doc as any).lastAutoTable.finalY + 12;
  }

  // Acknowledgements Table
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  yPos = addSectionTitle(doc, 'Acknowledgements', yPos);

  const ackTableData: string[][] = [];
  auditPolicies.forEach(policy => {
    policy.versions.forEach(version => {
      version.acknowledgements.forEach(ack => {
        const isPending = ack.version_status === 'pending_approval';
        ackTableData.push([
          policy.name,
          `v${version.version_number}`,
          ack.version_status.charAt(0).toUpperCase() + ack.version_status.slice(1).replace('_', ' '),
          ack.acknowledged_by_name,
          ack.acknowledged_by_email,
          format(new Date(ack.acknowledged_at), 'PP p'),
          isPending ? '(pending approval - may be invalidated)' : '',
        ]);
      });
    });
  });

  if (ackTableData.length === 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.mediumGray);
    doc.text('No acknowledgements recorded.', 14, yPos + 5);
    yPos += 15;
  } else {
    autoTable(doc, {
      startY: yPos,
      head: [['Policy', 'Version', 'Version Status', 'Acknowledged By', 'Email', 'Date', 'Note']],
      body: ackTableData,
      theme: 'striped',
      headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7, textColor: COLORS.darkGray },
      margin: { left: 14, right: 14, bottom: 30 },
      didParseCell: (data) => {
        // Highlight pending acknowledgements
        if (data.section === 'body' && data.column.index === 6) {
          const noteText = data.cell.raw as string;
          if (noteText.includes('pending')) {
            data.cell.styles.textColor = COLORS.amber;
            data.cell.styles.fontStyle = 'italic';
          }
        }
      },
    });
    yPos = (doc as any).lastAutoTable.finalY + 12;
  }

  // Ownership History Table
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  yPos = addSectionTitle(doc, 'Ownership History', yPos);

  if (ownershipChanges.length === 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.mediumGray);
    doc.text('No ownership changes recorded.', 14, yPos + 5);
  } else {
    const ownershipTableData = ownershipChanges.map(change => [
      change.policy_name,
      change.previous_owner_name ?? 'N/A (Initial)',
      change.new_owner_name,
      change.changed_by_name,
      format(new Date(change.changed_at), 'PP p'),
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Policy', 'Previous Assignee', 'New Assignee', 'Changed By', 'Date']],
      body: ownershipTableData,
      theme: 'striped',
      headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: COLORS.darkGray },
      margin: { left: 14, right: 14, bottom: 30 },
    });
  }

  // Add branded footer to all pages
  addBrandedFooter(doc, clinicName);

  // Download file with clinic name in filename
  const safeClinicName = clinicName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  doc.save(`${safeClinicName}_compliance-audit_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}

export function exportPolicySummaryPdf(policies: AuditPolicyData[], clinicName: string, ackStats?: Map<string, { pendingCount: number }>) {
  const doc = new jsPDF();
  
  // Filter out draft policies from audit export
  const auditPolicies = policies.filter(p => p.status !== 'draft');
  
  // Header
  let yPos = addBrandedHeader(doc, 'Policy Summary', clinicName);
  
  // Compliance Overview
  yPos = addSectionTitle(doc, 'Compliance Overview', yPos);
  
  const complianceCounts = { overdue: 0, due_soon: 0, needs_ack: 0, current: 0 };
  auditPolicies.forEach(p => {
    const pendingAckCount = p.requires_acknowledgement
      ? (ackStats?.get(p.id)?.pendingCount ?? 1)
      : 0;
    const status = getComplianceStatus(p, pendingAckCount);
    complianceCounts[status]++;
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Status', 'Count', 'Status', 'Count']],
    body: [
      ['Current', complianceCounts.current.toString(), 'Due Soon', complianceCounts.due_soon.toString()],
      ['Needs Acknowledgment', complianceCounts.needs_ack.toString(), 'Over Due', complianceCounts.overdue.toString()],
    ],
    theme: 'plain',
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: COLORS.darkGray },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { cellWidth: 25 },
      2: { fontStyle: 'bold', cellWidth: 45 },
      3: { cellWidth: 25 },
    },
    margin: { left: 14, right: 14, bottom: 30 },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const cellText = data.cell.raw as string;
        if (cellText === 'Current') data.cell.styles.textColor = COLORS.green;
        else if (cellText === 'Due Soon') data.cell.styles.textColor = COLORS.amber;
        else if (cellText === 'Needs Acknowledgment') data.cell.styles.textColor = COLORS.teal;
        else if (cellText === 'Over Due') data.cell.styles.textColor = COLORS.red;
      }
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 12;

  // Policy table
  yPos = addSectionTitle(doc, 'Active Policies', yPos);

  const tableData = auditPolicies.map((p, index) => {
    const pendingAckCount = p.requires_acknowledgement
      ? (ackStats?.get(p.id)?.pendingCount ?? 1)
      : 0;
    const complianceStatus = getComplianceStatus(p, pendingAckCount);
    return [
      (index + 1).toString(),
      p.name,
      p.category,
      getDisplayStatus(p.status),
      getComplianceStatusLabel(complianceStatus),
      p.owner_name,
      formatCadence(p.review_cadence_months),
      format(new Date(p.effective_date), 'PP'),
      format(new Date(p.next_review_date), 'PP'),
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['#', 'Policy Name', 'Category', 'Status', 'Review Status', 'Assigned To', 'Cadence', 'Effective', 'Next Review']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: COLORS.navy, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 7, textColor: COLORS.darkGray },
    columnStyles: {
      0: { cellWidth: 8 },
      1: { cellWidth: 35 },
      2: { cellWidth: 20 },
      3: { cellWidth: 18 },
      4: { cellWidth: 18 },
      5: { cellWidth: 22 },
      6: { cellWidth: 16 },
      7: { cellWidth: 20 },
      8: { cellWidth: 20 },
    },
    margin: { left: 14, right: 14, bottom: 30 },
    didParseCell: (data) => {
      // Color-code Review Status column
      if (data.section === 'body' && data.column.index === 4) {
        const statusText = data.cell.raw as string;
        if (statusText === 'Over Due') {
          data.cell.styles.textColor = COLORS.red;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Due Soon') {
          data.cell.styles.textColor = COLORS.amber;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Needs Ack') {
          data.cell.styles.textColor = COLORS.teal;
          data.cell.styles.fontStyle = 'bold';
        } else if (statusText === 'Current') {
          data.cell.styles.textColor = COLORS.green;
        }
      }
    },
  });

  // Add branded footer
  addBrandedFooter(doc, clinicName);

  // Download
  const safeClinicName = clinicName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  doc.save(`${safeClinicName}_policy-summary_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}
